import { forwardRef, useImperativeHandle } from "react";
import { motion, useAnimate } from "motion/react";
import type { AnimatedIconHandle, AnimatedIconProps } from "./types";

export const ChartLineIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "" }, ref) => {
    const [scope, animate] = useAnimate();
    const start = () => {
      animate(".chart-line", { pathLength: [0, 1] }, { duration: 0.6, ease: "easeInOut" });
      animate(".base-line", { scaleX: [0, 1] }, { duration: 0.4, ease: "easeOut" });
    };
    const stop = () => {
      animate(".chart-line", { pathLength: 1 }, { duration: 0.2 });
      animate(".base-line", { scaleX: 1 }, { duration: 0.2 });
    };
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    return (
      <motion.svg ref={scope} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={start} onHoverEnd={stop}>
        <motion.path d="M4 19l16 0" className="base-line" style={{ transformOrigin: "4px 19px" }} />
        <motion.path d="M4 15l4 -6l4 2l4 -5l4 4" className="chart-line" initial={{ pathLength: 1 }} />
      </motion.svg>
    );
  },
);
ChartLineIcon.displayName = "ChartLineIcon";

export const ChartPieIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "" }, ref) => {
    const [scope, animate] = useAnimate();
    const start = () => {
      animate(".pie-ring", { rotate: 90, scale: [1, 1.08, 1] }, { duration: 0.5, ease: "easeInOut" });
      animate(".pie-slice", { pathLength: [0.5, 1] }, { duration: 0.4 });
    };
    const stop = () => {
      animate(".pie-ring", { rotate: 0, scale: 1 }, { duration: 0.25 });
      animate(".pie-slice", { pathLength: 1 }, { duration: 0.2 });
    };
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    return (
      <motion.svg ref={scope} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={start} onHoverEnd={stop}>
        <motion.g className="pie-ring" style={{ transformOrigin: "12px 12px" }}>
          <motion.path d="M12 2a10 10 0 1 0 10 10" className="pie-slice" pathLength={1} />
          <path d="M12 2v10l7 3" />
        </motion.g>
      </motion.svg>
    );
  },
);
ChartPieIcon.displayName = "ChartPieIcon";

export const UsersIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "" }, ref) => {
    const [scope, animate] = useAnimate();
    const start = () => {
      animate(".user-left", { x: [-2, 0] }, { duration: 0.35 });
      animate(".user-right", { x: [2, 0] }, { duration: 0.35 });
    };
    const stop = () => {
      animate(".user-left, .user-right", { x: 0 }, { duration: 0.2 });
    };
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    return (
      <motion.svg ref={scope} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={start} onHoverEnd={stop}>
        <motion.g className="user-left">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </motion.g>
        <motion.g className="user-right">
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </motion.g>
      </motion.svg>
    );
  },
);
UsersIcon.displayName = "UsersIcon";

export const SparklesIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "" }, ref) => {
    const [scope, animate] = useAnimate();
    const start = () => {
      animate(".sparkle-main", { rotate: 180, scale: [1, 1.2, 1] }, { duration: 0.6 });
      animate(".sparkle-top", { rotate: -90, scale: [1, 0.8, 1.1] }, { duration: 0.5, delay: 0.1 });
    };
    const stop = () => {
      animate(".sparkle-main, .sparkle-top", { rotate: 0, scale: 1 }, { duration: 0.25 });
    };
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    return (
      <motion.svg ref={scope} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={start} onHoverEnd={stop}>
        <motion.path className="sparkle-top" d="M16 6a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2z" style={{ transformOrigin: "18px 6px" }} />
        <motion.path className="sparkle-main" d="M9 18a6 6 0 0 1 6 -6a6 6 0 0 1 -6 -6a6 6 0 0 1 -6 6a6 6 0 0 1 6 6z" style={{ transformOrigin: "9px 12px" }} />
      </motion.svg>
    );
  },
);
SparklesIcon.displayName = "SparklesIcon";

export const TrendingIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "" }, ref) => {
    const [scope, animate] = useAnimate();
    const start = () => animate(".trend-line", { pathLength: [0, 1] }, { duration: 0.5 });
    const stop = () => animate(".trend-line", { pathLength: 1 }, { duration: 0.2 });
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    return (
      <motion.svg ref={scope} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={start} onHoverEnd={stop}>
        <motion.path className="trend-line" d="M3 17l6 -6l4 4l8 -8" pathLength={1} />
        <path d="M14 7l7 0l0 7" />
      </motion.svg>
    );
  },
);
TrendingIcon.displayName = "TrendingIcon";

export const HeartbeatIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "" }, ref) => {
    const [scope, animate] = useAnimate();
    const start = () => {
      animate(".pulse-line", { pathLength: [0, 1], opacity: [0.4, 1] }, { duration: 0.55, ease: "easeInOut" });
      animate(".pulse-dot", { scale: [1, 1.35, 1], opacity: [0.6, 1, 0.8] }, { duration: 0.45, ease: "easeOut" });
    };
    const stop = () => {
      animate(".pulse-line", { pathLength: 1, opacity: 1 }, { duration: 0.2 });
      animate(".pulse-dot", { scale: 1, opacity: 1 }, { duration: 0.2 });
    };
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    return (
      <motion.svg
        ref={scope}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onHoverStart={start}
        onHoverEnd={stop}
      >
        <motion.path d="M4 12h2.5l1.5-4.5L11 16l2-4.5H20" className="pulse-line" pathLength={1} />
        <motion.circle cx="20" cy="11.5" r="1.25" className="pulse-dot" fill={color} stroke="none" style={{ transformOrigin: "20px 11.5px" }} />
      </motion.svg>
    );
  },
);
HeartbeatIcon.displayName = "HeartbeatIcon";
